import React, {
  ComponentType,
  useCallback,
  useId,
  useMemo,
  useState,
} from "react";

import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import DatePickerJs from "../../../shared/DatePicker/DatePicker";
import ExportToXlsxButtonJs from "../../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";
import Button from "../../../shared/Button/Button";
import Select from "../../../shared/Select/Select";
import {
  formatCompactCurrencyTick,
  formatCurrency,
} from "../../../../utils/financialFormatters";
import { getAdminNow } from "../../../../utils/dateService";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { DATE_RANGE } from "../../../../utils/Constants";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../../utils/narrowViewportChartDates";
import {
  DEFAULT_TOP_RANKING_LIMIT,
  TOP_RANKING_BAR_ROW_HEIGHT_PX,
  TOP_RANKING_LIMIT_OPTIONS,
  TOP_RANKING_MIN_CHART_HEIGHT_PX,
  TOP_RANKING_SCROLL_THRESHOLD,
} from "../../../../utils/kpiRankingMetric";
import {
  createRankingBarDatalabelsPlugin,
  formatRankingPercentChange,
} from "../../../../utils/kpiRankingDatalabels";
import { BarChart, ChartCard, CHART_PALETTE } from "../../../shared/Charts";
import type { CasinoTopFiveRow } from "../casinoKpisV2MockData";
import type {
  CasinoRankingRow,
  CasinoTopFiveData,
  CasinoTopFiveMetricKey,
} from "../../../../queries/casino-kpis/normalizeCasinoRankingRows";
import {
  getMetricPercentChange,
  getMetricValue,
} from "../../../../queries/casino-kpis/normalizeCasinoRankingRows";
import { useCurrencySymbol } from "../../../../hooks/useCurrencySymbol";
import type { CasinoKpiFilterOption } from "../../../../queries/casino-kpis/casinoKpiFilters";

import styles from "./CasinoTopFivePanel.module.css";

const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

export type { CasinoTopFiveMetricKey };

const CURRENCY_METRICS = new Set<CasinoTopFiveMetricKey>([
  "turnover",
  "ggr",
]);

const NOOP_GET_DATA = (_queryString: string | null): void => undefined;

type UseTopFiveResult = {
  data?: CasinoTopFiveData;
  isFetching?: boolean;
  isLoading?: boolean;
  isError?: boolean;
};

const NO_LIVE_DATA: UseTopFiveResult = {};

export type CasinoTopFivePanelProps = {
  title: string;
  /** Display rows already grouped by metric (mock / static data). */
  dataByMetric?: Record<string, CasinoTopFiveRow[]>;
  /**
   * Optional live-data hook. When provided, the panel calls it with the
   * date filter + limit and reads `byMetric` off the result. Wins over
   * `dataByMetric` whenever it returns a defined `byMetric`.
   */
  useData?: (filters: {
    start_date: string;
    end_date: string;
    limit: number;
    money_type?: string[];
  }) => UseTopFiveResult;
  exportFilename: string;
  moneyTypeOptions?: CasinoKpiFilterOption[];
};

type DisplayRow = CasinoTopFiveRow & { percent_change: number | null };

const rankingToDisplayRows = (
  rows: CasinoRankingRow[],
  metric: CasinoTopFiveMetricKey,
): DisplayRow[] =>
  rows.map((r) => ({
    name: r.name,
    value: getMetricValue(r, metric),
    percent_change: getMetricPercentChange(r, metric),
  }));

const CasinoTopFivePanel: React.FC<CasinoTopFivePanelProps> = ({
  title,
  dataByMetric,
  useData,
  exportFilename,
  moneyTypeOptions = [],
}) => {
  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );
  const [metric, setMetric] = useState<CasinoTopFiveMetricKey>("turnover");
  const [limit, setLimit] = useState<number>(DEFAULT_TOP_RANKING_LIMIT);
  const [moneyTypes, setMoneyTypes] = useState<string[]>([]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const filtersId = useId();

  const currencySymbol = useCurrencySymbol() ?? "$";

  const filters = useMemo(
    () => ({
      start_date: dates[0]?.format("YYYY-MM-DD") ?? "",
      end_date: dates[1]?.format("YYYY-MM-DD") ?? "",
      limit,
      money_type: moneyTypes,
    }),
    [dates, limit, moneyTypes],
  );

  const liveResult: UseTopFiveResult = (useData ?? (() => NO_LIVE_DATA))(
    filters,
  );
  const isError = Boolean(liveResult.isError);
  const showLoading = Boolean(liveResult.isFetching || liveResult.isLoading);

  const liveRows = useMemo<DisplayRow[] | null>(() => {
    if (!useData) return null;
    const byMetric = liveResult.data?.byMetric;
    if (!byMetric) return null;
    const ranking = byMetric[metric];
    return ranking ? rankingToDisplayRows(ranking, metric) : [];
  }, [useData, liveResult.data, metric]);

  const rows = useMemo<DisplayRow[]>(() => {
    if (liveRows) return liveRows;
    return (dataByMetric?.[metric] ?? []).map((r) => ({
      ...r,
      percent_change: null,
    }));
  }, [liveRows, dataByMetric, metric]);
  const isScrollable = rows.length > TOP_RANKING_SCROLL_THRESHOLD;
  const chartHeight = isScrollable
    ? Math.max(
        TOP_RANKING_MIN_CHART_HEIGHT_PX,
        rows.length * TOP_RANKING_BAR_ROW_HEIGHT_PX,
      )
    : TOP_RANKING_MIN_CHART_HEIGHT_PX;

  // `total_bets` is intentionally absent — the metric is collapsed into
  // `total_rounds` on this surface (one bet == one round in the casino
  // domain), so showing both was redundant per product spec.
  const metricOptions: { value: CasinoTopFiveMetricKey; label: string }[] = [
    { value: "turnover", label: AppStrings.TURNOVER },
    { value: "ggr", label: AppStrings.GGR },
    { value: "margin_pct", label: AppStrings.MARGIN_PCT },
    { value: "total_rounds", label: AppStrings.TOTAL_ROUNDS },
    { value: "total_users", label: AppStrings.TOTAL_ACTIVE_USERS },
  ];

  const limitOptions = TOP_RANKING_LIMIT_OPTIONS.map((value) => ({
    value,
    label: String(value),
  }));

  const formatValue = useCallback(
    (value: number) => {
      if (metric === "margin_pct") return `${value}%`;
      if (CURRENCY_METRICS.has(metric))
        return formatCurrency(value, currencySymbol);
      return Intl.NumberFormat("en-US").format(value);
    },
    [metric, currencySymbol],
  );

  const exportRows = useMemo(
    () =>
      rows.map((r) => ({
        name: r.name,
        value: r.value,
        percent_change: r.percent_change,
        metric,
      })),
    [rows, metric],
  );

  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => r.name),
      datasets: [
        {
          data: rows.map((r) => r.value),
          backgroundColor: rows.map(
            (_, idx) => CHART_PALETTE.bars[idx % CHART_PALETTE.bars.length],
          ),
          borderRadius: 4,
          maxBarThickness: 22,
        },
      ],
    }),
    [rows],
  );

  const rankingDatalabelsPlugin = useMemo(
    () =>
      createRankingBarDatalabelsPlugin(
        (dataIndex) => rows[dataIndex]?.percent_change,
        formatValue,
      ),
    [rows, formatValue],
  );

  const chartOptions = useMemo(
    () => ({
      layout: { padding: { right: 96 } },
      scales: {
        x: {
          beginAtZero: true,
          grid: { display: true },
          ticks: {
            callback: (value: number | string) =>
              CURRENCY_METRICS.has(metric)
                ? formatCompactCurrencyTick(Number(value), currencySymbol)
                : value,
          },
        },
        y: {
          grid: { display: false },
          ticks: { font: { size: 12 } },
        },
      },
      plugins: {
        tooltip: {
          displayColors: false,
          callbacks: {
            label: (item: { raw: unknown; dataIndex: number }) => {
              const valueLabel = formatValue(Number(item.raw));
              const pct = rows[item.dataIndex]?.percent_change;
              if (pct == null) return valueLabel;
              return `${valueLabel} (${formatRankingPercentChange(pct)})`;
            },
          },
        },
        // Dual-tone labels are drawn by `rankingDatalabelsPlugin` (white
        // value + green/red %). chartjs-plugin-datalabels can't split colors.
        datalabels: { display: false },
      },
    }),
    [currencySymbol, formatValue, metric, rows],
  );

  const showEmpty = !showLoading && !isError && rows.length === 0;

  return (
    <ChartCard
      title={title}
      headerExtra={
        <>
          <span className={styles.filterToggle}>
            <Button
              onClick={() => setFiltersVisible((visible) => !visible)}
              aria-expanded={filtersVisible}
              aria-controls={filtersId}
              aria-label={AppStrings.PAM_REPORT_FILTERS}
            >
              <Filter size={18} aria-hidden="true" />
              {filtersVisible ? (
                <ChevronUp size={16} aria-hidden="true" />
              ) : (
                <ChevronDown size={16} aria-hidden="true" />
              )}
            </Button>
          </span>
          <div
            id={filtersId}
            className={`${styles.headerSelects}${
              filtersVisible ? "" : ` ${styles.filtersCollapsed}`
            }`}
            data-testid="casino-top-five-filters"
          >
            <Select
              className={styles.limitSelect}
              value={limit}
              options={limitOptions}
              onChange={(value) => setLimit(Number(value))}
              aria-label={AppStrings.PAM_TOP_RANKING_LIMIT}
            />
            <Select
              className={styles.metricSelect}
              value={metric}
              options={metricOptions}
              onChange={(value) => setMetric(value as CasinoTopFiveMetricKey)}
              aria-label={AppStrings.METRIC}
            />
            <Select
              className={styles.moneyTypeSelect}
              mode="multiple"
              value={moneyTypes}
              options={moneyTypeOptions}
              onChange={(value) => setMoneyTypes(value as string[])}
              aria-label={AppStrings.CASINO_KPI_CURRENCY_TYPE}
              placeholder={AppStrings.CASINO_KPI_ALL_CURRENCY_TYPES}
              maxTagCount="responsive"
              showSearch
              optionFilterProp="label"
            />
          </div>
        </>
      }
      bodyClassName={styles.chartContainer}
      controls={
        <>
          <CustomDatePicker
            getData={NOOP_GET_DATA}
            dates={dates}
            setDates={setDates}
            showPresets={true}
            startRange={startRange}
          />
          <ExportToXlsxButton
            data={exportRows}
            filename={exportFilename}
          />
        </>
      }
    >
      {isError ? (
        <p
          className={styles.errorBanner}
          role="alert"
          data-testid="casino-top-five-error"
        >
          {AppStrings.CASINO_TOP_FIVE_LOAD_ERROR}
        </p>
      ) : showEmpty ? (
        <p className={styles.emptyMessage} data-testid="casino-top-five-empty">
          {AppStrings.PAM_CHART_EMPTY_STATE}
        </p>
      ) : (
        <div
          className={styles.chartViewport}
          aria-label={title}
          tabIndex={isScrollable ? 0 : undefined}
          data-testid="casino-top-five-viewport"
        >
          <div
            className={styles.chartCanvas}
            style={{ height: `${chartHeight}px` }}
            data-testid="casino-top-five-canvas"
          >
            <BarChart
              horizontal
              data={chartData}
              optionsOverride={chartOptions}
              plugins={[rankingDatalabelsPlugin as never]}
              isLoading={showLoading}
            />
          </div>
        </div>
      )}
    </ChartCard>
  );
};

export default CasinoTopFivePanel;
